<template>
  <div class="hp-Wrapper">
    <HomepageSidebar v-if="canExploreOtherPeopleProgress" />
    <HomePageContent />
  </div>
</template>

<script setup>
import { isEmpty, isNull } from 'lodash'
import { computed, nextTick, onMounted, provide, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

import HomepageGroupsApiHandler from '@/api/homepage-groups'
import HomepageUsersApiHandler from '@/api/homepage-users'
import IntervalsInfoApiHandler from '@/api/intervals-info'
import { handleError } from '@/utils/error-handling'
import { GLOBAL_GROUP_KEYS } from '@/utils/global-groups'
import {
  HOME_PAGE_INTERVAL_FILTER_VALUES,
  resolveHomepageIntervalFilters,
  useHomepageFiltersPersistence,
  useHomepageProgressExploring,
  useRouteGradeTypes,
  useRouteWorkspaceIds
} from '@/utils/homepage'
import { homePageStateInjectionKey } from '@/utils/injection-keys'
import { NEW_HP_FILTERS_KEYS, restoreFilterValue } from '@/utils/okr-elements/filters'

import HomePageContent from '@/components/home-page/HomePageContent'
import HomepageSidebar from '@/components/home-page/HomepageSidebar'

defineOptions({
  name: 'HomePage'
})

const route = useRoute()
const store = useStore()

const { ALL_CURRENT } = HOME_PAGE_INTERVAL_FILTER_VALUES

//#region Active intervals
const allActiveIntervals = ref([])

const getAllActiveIntervals = async () => {
  const intervalsInfoApi = new IntervalsInfoApiHandler()
  try {
    const { intervals } = await intervalsInfoApi.getAllActiveIntervals()
    allActiveIntervals.value = intervals || []
  } catch (error) {
    handleError({ error })
  }
}
//#endregion

//#region Home page state
const filterValues = ref({
  [NEW_HP_FILTERS_KEYS.WORKSPACE_IDS]: [],
  [NEW_HP_FILTERS_KEYS.INTERVAL_TYPE_ID]: null,
  [NEW_HP_FILTERS_KEYS.START_DATES]: [],
  [NEW_HP_FILTERS_KEYS.DUE_DATES]: [],
  [NEW_HP_FILTERS_KEYS.GRADE_TYPES]: [],
  [NEW_HP_FILTERS_KEYS.SELECTED_USER]: null,
  [NEW_HP_FILTERS_KEYS.SELECTED_GROUP]: null
})

const homeFiltersResolved = ref({
  [NEW_HP_FILTERS_KEYS.WORKSPACE_IDS]: false,
  [NEW_HP_FILTERS_KEYS.INTERVAL_TYPE_ID]: false,
  [NEW_HP_FILTERS_KEYS.GRADE_TYPES]: false,
  [NEW_HP_FILTERS_KEYS.SELECTED_USER]: false,
  [NEW_HP_FILTERS_KEYS.SELECTED_GROUP]: false
})

const areFiltersValuesResolved = computed(() => {
  return Object.values(homeFiltersResolved.value).every(value => value)
})

const selectedIntervalsByWorkspaces = computed(() => {
  return allActiveIntervals.value.filter(interval => {
    return filterValues.value[NEW_HP_FILTERS_KEYS.WORKSPACE_IDS].includes(interval.workspaceId)
  })
})

const selectedIntervalIds = computed(() => {
  const selectedIntervalTypeId = filterValues.value[NEW_HP_FILTERS_KEYS.INTERVAL_TYPE_ID]

  return selectedIntervalsByWorkspaces.value
    .filter(interval => {
      if (selectedIntervalTypeId === ALL_CURRENT) {
        return true
      }
      return interval.typeId === filterValues.value[NEW_HP_FILTERS_KEYS.INTERVAL_TYPE_ID]
    })
    .map(interval => interval.id)
})

const homePageState = {
  filterValues,
  selectedIntervalIds,
  homeFiltersResolved,
  areFiltersValuesResolved
}

provide(homePageStateInjectionKey, homePageState)
//#endregion

//#region Restore filters
const { resolveHomepageWorkspaceIds } = useRouteWorkspaceIds()

// the flags open one tick after the values: the filters watcher in HomePageContent is gated by
// areFiltersValuesResolved and must not see the restore as a change to write, the first write
// belongs to the watcher on the flag below
const restoreWorkspaceFilter = () => {
  filterValues.value[NEW_HP_FILTERS_KEYS.WORKSPACE_IDS] = resolveHomepageWorkspaceIds()

  nextTick(() => {
    homeFiltersResolved.value[NEW_HP_FILTERS_KEYS.WORKSPACE_IDS] = true
  })
}

const restoreIntervalFilters = () => {
  const { intervalTypeId, startDates, dueDates } = resolveHomepageIntervalFilters({
    intervalTypeId: restoreFilterValue(route, NEW_HP_FILTERS_KEYS.INTERVAL_TYPE_ID, null),
    startDates: restoreFilterValue(route, NEW_HP_FILTERS_KEYS.START_DATES, null),
    dueDates: restoreFilterValue(route, NEW_HP_FILTERS_KEYS.DUE_DATES, null)
  })

  filterValues.value[NEW_HP_FILTERS_KEYS.INTERVAL_TYPE_ID] = intervalTypeId
  filterValues.value[NEW_HP_FILTERS_KEYS.START_DATES] = startDates
  filterValues.value[NEW_HP_FILTERS_KEYS.DUE_DATES] = dueDates

  nextTick(() => {
    homeFiltersResolved.value[NEW_HP_FILTERS_KEYS.INTERVAL_TYPE_ID] = true
  })
}

const { resolveHomepageGradeTypes } = useRouteGradeTypes()
const restoreGradeTypeFilter = () => {
  filterValues.value[NEW_HP_FILTERS_KEYS.GRADE_TYPES] = resolveHomepageGradeTypes()

  nextTick(() => {
    homeFiltersResolved.value[NEW_HP_FILTERS_KEYS.GRADE_TYPES] = true
  })
}

const groupsApi = new HomepageGroupsApiHandler()
const usersApi = new HomepageUsersApiHandler()

const userData = computed(() => store.state.system.userData)

const currentUserId = computed(() => userData.value.userAccountId)

const firstUserGroupId = computed(() => {
  const [firstGroup = {}] = store.state.system.userData.userGroups || []

  return firstGroup[GLOBAL_GROUP_KEYS.ID] ?? null
})

// candidates in priority order: saved group -> first user group -> first available group
// returns null only when there are no groups at all
const resolveGroupId = async savedGroupId => {
  if (savedGroupId && (await groupsApi.isGroupExists(savedGroupId))) {
    return savedGroupId
  }

  if (firstUserGroupId.value && (await groupsApi.isGroupExists(firstUserGroupId.value))) {
    return firstUserGroupId.value
  }

  const firstAvailableGroup = await groupsApi.getFirstAvailableGroup()

  return firstAvailableGroup?.[GLOBAL_GROUP_KEYS.ID] ?? null
}

// current user is always valid, any other saved user is checked via api
// falls back to the current user when the saved one no longer exists
const resolveUserId = async savedUserId => {
  if (savedUserId === currentUserId.value) {
    return savedUserId
  }

  if (await usersApi.isUserExists(savedUserId)) {
    return savedUserId
  }

  return currentUserId.value
}

const setGroupAndUserFilters = ({ groupId = null, userId = null } = {}) => {
  filterValues.value[NEW_HP_FILTERS_KEYS.SELECTED_GROUP] = groupId
  filterValues.value[NEW_HP_FILTERS_KEYS.SELECTED_USER] = userId

  nextTick(() => {
    homeFiltersResolved.value[NEW_HP_FILTERS_KEYS.SELECTED_GROUP] = true
    homeFiltersResolved.value[NEW_HP_FILTERS_KEYS.SELECTED_USER] = true
  })
}

const { canExploreOtherPeopleProgress } = useHomepageProgressExploring()

// migration from the user-centric homepage: the saved user is honored only when
// there is no saved group, otherwise the group is the entry point (saved or not)
const restoreGroupAndUserFilters = async () => {
  const restoredGroup = canExploreOtherPeopleProgress.value
    ? restoreFilterValue(route, NEW_HP_FILTERS_KEYS.SELECTED_GROUP, null)
    : null

  const restoredUser = canExploreOtherPeopleProgress.value
    ? restoreFilterValue(route, NEW_HP_FILTERS_KEYS.SELECTED_USER, null)
    : currentUserId.value

  if (!restoredGroup && restoredUser) {
    try {
      const userId = await resolveUserId(restoredUser)

      setGroupAndUserFilters({ userId })
    } catch (error) {
      handleError({ error })
      setGroupAndUserFilters({ userId: currentUserId.value })
    }
    return
  }

  try {
    const groupId = await resolveGroupId(restoredGroup)

    if (isNull(groupId)) {
      setGroupAndUserFilters({ userId: currentUserId.value })
    } else {
      setGroupAndUserFilters({ groupId })
    }
  } catch (error) {
    handleError({ error })
    setGroupAndUserFilters({ userId: currentUserId.value })
  }
}

// TODO: implement restore filters for other filters

const restoreFilters = () => {
  restoreWorkspaceFilter()
  restoreIntervalFilters()
  restoreGradeTypeFilter()
  restoreGroupAndUserFilters()
}

const autoPinWasPerformed = computed(() => {
  return store.getters['system/homepageAutoPinStatus']
})

const pinnedGroupIds = computed(() => {
  return store.getters['system/homepagePinnedGroups']
})

const pinnedUserIds = computed(() => {
  return store.getters['system/homepagePinnedUsers']
})

const runAutomaticPin = async () => {
  if (!canExploreOtherPeopleProgress.value) return

  if (autoPinWasPerformed.value) return

  const hasPins = !isEmpty(pinnedGroupIds.value) || !isEmpty(pinnedUserIds.value)

  if (hasPins) {
    await store.dispatch('system/markAutoPinPerformed')

    return
  }

  const groups = (store.state.system.userData.userGroups || [])
    .map(group => group[GLOBAL_GROUP_KEYS.ID])
    .slice(0, 2)

  await store.dispatch('system/applyAutoPins', {
    users: [currentUserId.value],
    groups
  })
}

const initData = async () => {
  await Promise.all([getAllActiveIntervals(), runAutomaticPin()])

  restoreFilters()
}

const { persistFilters } = useHomepageFiltersPersistence(filterValues)

// the first write right after the restore, so URL and LS hold the normalized state
watch(areFiltersValuesResolved, areResolved => {
  if (!areResolved) {
    return
  }

  persistFilters()
})
//#endregion

onMounted(() => {
  initData()
})
</script>

<style lang="scss" scoped>
.hp-Wrapper {
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: row;
}
</style>
